import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Loader2, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { Site } from '../types';
import { callSiteApi } from '../utils';
import { useToast } from '@/hooks/use-toast';

interface PostEditModalProps {
  site: Site;
  post: any;
  postType?: string;
  onClose: () => void;
  onSave: () => void;
}

export function PostEditModal({ site, post, postType, onClose, onSave }: PostEditModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: post.title || '',
    slug: post.slug || '',
    status: post.status,
    author: post.author_id || '',
  });
  const [users, setUsers] = useState<any[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(post.featured_image_url || null);
  const [removeImage, setRemoveImage] = useState(false);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 2 * 1024 * 1024) {
        toast({ title: 'File too large', description: 'Max size is 2MB.', variant: 'destructive' });
        return;
      }
      setImageFile(file);
      setRemoveImage(false);
      
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await callSiteApi(site.url, site.publicKey, site.secretKey, 'users?number=100');
        if (res && res.users) {
          setUsers(res.users);
        }
      } catch (e) {
        console.error('Failed to fetch users', e);
      }
    };
    fetchUsers();
  }, [site]);

  const uploadMedia = async (): Promise<number | null> => {
    if (!imageFile) return null;
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target?.result as string;
          const res = await callSiteApi(
            site.url, site.publicKey, site.secretKey, 
            'content/media/upload', 'POST', 
            JSON.stringify({ file_name: imageFile.name, file_data: base64Data })
          );
          resolve(res.attachment_id);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsDataURL(imageFile);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let thumbnailIdStr = undefined;
      
      if (removeImage) {
        thumbnailIdStr = '';
      } else if (imageFile) {
        const attachId = await uploadMedia();
        if (attachId) {
          thumbnailIdStr = String(attachId);
        }
      }

      await callSiteApi(
        site.url, site.publicKey, site.secretKey, 
        `content/posts/${post.id}`, 'PUT', 
        JSON.stringify({ 
          post_title: formData.title,
          post_name: formData.slug,
          post_status: formData.status,
          post_author: formData.author,
          ...(thumbnailIdStr !== undefined ? { _thumbnail_id: thumbnailIdStr } : {})
        })
      );
      
      toast({ title: 'Post updated successfully' });
      onSave();
    } catch (error) {
      toast({ title: 'Update failed', description: 'Could not save post.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-2xl bg-card border border-border shadow-2xl rounded-2xl overflow-hidden flex flex-col max-h-[90vh]"
      >
        <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-lg font-bold text-foreground">Edit <span className="capitalize">{postType || post.type || 'Post'}</span></h2>
            <p className="text-sm text-muted-foreground">ID: {post.id} • Last modified: {post.modified?.split(' ')[0]}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          <form id="post-edit-form" onSubmit={handleSubmit} className="space-y-6">
            
            {/* Core Fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Title</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full bg-background border border-transparent px-3 py-2 rounded-lg text-sm outline-none focus:outline-none focus:ring-0 focus:border-transparent transition-shadow shadow-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Slug</label>
                <div className="flex bg-muted/30 border border-transparent rounded-lg overflow-hidden group outline-none focus-within:outline-none focus-within:ring-0 focus-within:border-transparent transition-shadow shadow-none">
                  <span className="px-3 py-2 text-sm text-muted-foreground bg-muted border-r border-transparent select-none">/</span>
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                    className="w-full bg-transparent px-3 py-2 text-sm focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full bg-background border border-transparent px-3 py-2 rounded-lg text-sm outline-none focus:outline-none focus:ring-0 focus:border-transparent transition-shadow shadow-none"
                  >
                    <option value="publish">Published</option>
                    <option value="draft">Draft</option>
                    <option value="pending">Pending Review</option>
                    <option value="private">Private</option>
                    <option value="trash">Trash</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Author</label>
                  <select
                    value={formData.author}
                    onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                    className="w-full bg-background border border-transparent px-3 py-2 rounded-lg text-sm outline-none focus:outline-none focus:ring-0 focus:border-transparent transition-shadow shadow-none"
                  >
                    <option value="" disabled>Select an author</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.display_name} ({user.email})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Featured Image */}
            <div className="pt-2">
              <label className="block text-sm font-semibold mb-2">Featured Image</label>
              <div className="flex flex-col items-start gap-3">
                <div 
                  className={`w-full h-48 rounded-xl border-2 border-dashed flex flex-col items-center justify-center relative overflow-hidden group cursor-pointer transition-colors ${imagePreview ? 'border-transparent' : 'border-border hover:border-workspace-accent hover:bg-muted/50'}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {imagePreview ? (
                    <>
                       <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                       <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                         <div className="bg-white/20 p-2 rounded-full backdrop-blur-sm text-white">
                           <Upload className="w-5 h-5" />
                         </div>
                       </div>
                    </>
                  ) : (
                    <>
                      <ImageIcon className="w-8 h-8 text-muted-foreground/50 mb-2" />
                      <span className="text-sm font-medium text-muted-foreground">Set Featured Image</span>
                      <p className="text-xs text-muted-foreground/60 mt-1">Recommended size: 1200x630px</p>
                    </>
                  )}
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageChange} />
                </div>
                {imagePreview && (
                  <button 
                    type="button" 
                    onClick={() => { setImagePreview(null); setImageFile(null); setRemoveImage(true); }}
                    className="text-xs text-red-500 font-medium hover:text-red-400 flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove Image
                  </button>
                )}
              </div>
            </div>

          </form>
        </div>

        <div className="px-6 py-4 bg-muted/50 border-t border-border flex justify-end gap-3 rounded-b-2xl">
          <button 
            type="button" 
            onClick={onClose} 
            className="px-4 py-2 font-medium text-sm rounded-lg hover:bg-muted text-foreground transition-colors"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            form="post-edit-form"
            disabled={loading} 
            className="px-6 py-2 font-semibold text-sm rounded-lg bg-workspace-accent hover:bg-workspace-accent-hover text-white transition-colors flex items-center justify-center min-w-[100px]"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
